import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Chip, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageHeader, DataTable, flattenPages } from '../components';
import { useBulkImportsPaginated } from '../hooks/useBulkImport';
import { colors, formatCurrency, formatDateTime } from '../constants';

/**
 * History of bulk imports. Each row is one import run; opening it shows the
 * lines it applied. The "Bulk Import" action starts a new one.
 */
export default function Imports() {
  const history = useHistory();

  const params = useMemo(() => ({ limit: 20 }), []);
  const query = useBulkImportsPaginated(params);
  const rows = flattenPages(query);

  const columns = [
    {
      key: 'createdAt',
      label: 'Imported',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
          {formatDateTime(row.createdAt)}
        </Typography>
      ),
    },
    { key: 'totalRows', label: 'Items', align: 'right' },
    {
      key: 'result',
      label: 'Result',
      render: (row) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {row.createdCount > 0 && (
            <Chip size="small" color="success" label={`${row.createdCount} created`} />
          )}
          {row.updatedCount > 0 && (
            <Chip size="small" color="info" label={`${row.updatedCount} updated`} />
          )}
          {row.failedCount > 0 && (
            <Chip size="small" color="error" label={`${row.failedCount} failed`} />
          )}
        </Stack>
      ),
    },
    { key: 'totalQuantity', label: 'Quantity', align: 'right' },
    {
      key: 'totalCost',
      label: 'Total Cost',
      align: 'right',
      render: (row) => formatCurrency(row.totalCost),
    },
  ];

  return (
    <>
      <PageHeader
        title="Import"
        subtitle="Stock added in bulk, without a purchase invoice."
        actionLabel="Bulk Import"
        actionIcon={<AddIcon />}
        onAction={() => history.push('/inventory-management/import/new')}
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={query.isLoading}
        onRowClick={(row) => history.push(`/inventory-management/import/${row.uuid}`)}
        emptyTitle="No imports yet"
        emptyMessage="Use Bulk Import to add stock for many items at once."
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        fetchNextPage={query.fetchNextPage}
      />
    </>
  );
}
